import { motion } from "framer-motion";
import { Sparkles, ShieldX, Truck, Heart, RefreshCw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const ITEMS_EN = [
  { icon: Sparkles, title: "100% GIR Gaumata", text: "Milk sourced exclusively from pure-breed GIR Desi cows. Never mixed with HF or Jersey breeds. Authentic Gaumata milk — always." },
  { icon: ShieldX, title: "Zero Chemicals", text: "No preservatives, no hormones, no adulteration. Every morning, 100% pure and natural milk — our unwavering promise." },
  { icon: Truck, title: "Delivered from 6 AM", text: "Fresh milk delivered to your doorstep from 6 AM onwards — every single day, without fail." },
  { icon: Heart, title: "Rich A2 Protein", text: "A2 beta-casein protein — easier to digest, better for immunity, gut health, brain and memory." },
  { icon: RefreshCw, title: "Flexible Plans", text: "Daily, weekly or monthly — choose what suits you. Pause or cancel anytime. No lock-in, no hidden charges." },
];

const ITEMS_HI = [
  { icon: Sparkles, title: "100% GIR गौमाता", text: "केवल शुद्ध नस्ल की GIR देसी गायों का दूध। HF या जर्सी नस्ल का कभी मिश्रण नहीं। प्रामाणिक गौमाता दूध — सदैव।" },
  { icon: ShieldX, title: "शून्य रसायन", text: "कोई संरक्षक नहीं, कोई हार्मोन नहीं, कोई मिलावट नहीं। प्रतिदिन 100% शुद्ध और प्राकृतिक दूध — हमारा अटूट वचन।" },
  { icon: Truck, title: "प्रातः 6 बजे से डिलीवरी", text: "प्रतिदिन प्रातः 6 बजे से आपके द्वार पर ताज़ा दूध — बिना किसी अपवाद के।" },
  { icon: Heart, title: "A2 प्रोटीन से भरपूर", text: "A2 बीटा-केसीन प्रोटीन — सहज पाचनीय, रोग प्रतिरोधक शक्ति, पाचन तंत्र एवं स्मरण शक्ति के लिए लाभकारी।" },
  { icon: RefreshCw, title: "लचीली योजनाएं", text: "दैनिक, साप्ताहिक या मासिक — अपनी सुविधा अनुसार चुनें। कभी भी रोकें या रद्द करें। कोई बाध्यता नहीं।" },
];

function FeatureCard({ item, idx }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group bg-white border border-dd-line rounded-2xl p-7 md:p-8 hover:border-dd-gold shadow-[0_8px_30px_-12px_rgba(15,46,32,0.12)] hover:shadow-[0_20px_50px_-20px_rgba(201,162,76,0.4)] transition-all"
      data-testid={`why-dd-${idx}`}
    >
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-dd-gold-soft to-dd-gold-light/40 flex items-center justify-center group-hover:from-dd-gold/40 group-hover:to-dd-gold/20 transition-colors">
        <Icon className="w-6 h-6 text-[#8F6E28]" strokeWidth={1.5} />
      </div>
      <h3 className="mt-5 font-serif text-2xl text-dd-ink leading-tight">{item.title}</h3>
      <div className="mt-3 w-8 h-px bg-dd-gold/60" />
      <p className="mt-3 text-dd-ink-muted text-sm leading-relaxed">{item.text}</p>
    </motion.div>
  );
}

export default function WhyDivyaDhara() {
  const { lang, t } = useI18n();
  const items = lang === "hi" ? ITEMS_HI : ITEMS_EN;
  const topRow = items.slice(0, 3);
  const bottomRow = items.slice(3, 5);

  return (
    <section className="dd-section bg-dd-ivory" data-testid="why-dd-section">
      <div className="dd-container">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="dd-label text-dd-gold">{t.why.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-ink mt-3">{t.why.title}</h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-ink-muted">{t.why.subtitle}</p>
        </div>

        {/* Top row — 3 cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {topRow.map((it, idx) => (
            <FeatureCard key={it.title} item={it} idx={idx} />
          ))}
        </div>

        {/* Bottom row — 2 cards centered */}
        <div className="grid sm:grid-cols-2 gap-5 md:gap-6 mt-5 md:mt-6 max-w-[calc(66.666%-1rem)] mx-auto">
          {bottomRow.map((it, idx) => (
            <FeatureCard key={it.title} item={it} idx={idx + 3} />
          ))}
        </div>
      </div>
    </section>
  );
}
